/**
 * FloorControl: 발언권 요청/승인/반납 UI
 *
 * - 일반 참여자: 발언권 요청 / 반납 버튼 (notiwrap 팝업)
 * - 모더레이터: 요청 목록 + 수락/거부 버튼 + 전체 수락/거부
 *
 * 원본: FloorRequestPresenter + MasterFloorRequestPresenter
 */
import React from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { floorActions } from '@/store/slices/floorSlice';
import { isMasterUser } from '@/store/slices/masterSlice';

interface FloorControlProps {
  onRequestFloor: () => void;
  onGrantFloor: (userId: string) => void;
  onGrantFloorTarget: (userId: string, type: 'accept' | 'reject') => void;
  onStopFloor: () => void;
}

const FloorControl: React.FC<FloorControlProps> = ({
  onRequestFloor,
  onGrantFloor,
  onGrantFloorTarget,
  onStopFloor,
}) => {
  const dispatch = useAppDispatch();
  const floor = useAppSelector((s) => s.floor);
  const local = useAppSelector((s) => s.local);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);
  const participantMap = useAppSelector((s) => s.participants.participants);
  const participants = Object.values(participantMap);

  const isModerator = isMasterUser(masterUsers, local.wehagoId);

  // 발언권 모드 꺼져 있으면 일반 참여자는 제어 불가
  if (!floor.isEnabled && !isModerator) return null;

  // ─── 모더레이터 뷰: 발언권 요청 목록 ───────────────────────────────────────
  if (isModerator && floor.requestList.length > 0) {
    const handleAcceptAll = () => {
      for (const userId of floor.requestList) {
        onGrantFloorTarget(userId, 'accept');
      }
    };

    const handleRejectAll = () => {
      for (const userId of floor.requestList) {
        onGrantFloorTarget(userId, 'reject');
      }
    };

    return (
      <div className="notiwrap rtcCustoms_floorWrap">
        {/* 전체 수락/거부 헤더 */}
        {floor.requestList.length > 1 && (
          <div className="noti_header rtcCustoms_floorHeader">
            <span>발언권 요청 ({floor.requestList.length}명)</span>
            <div>
              <button type="button" onClick={handleAcceptAll} className="rtcCustoms_floorAcceptAll">전체 수락</button>
              <button type="button" className="reject" onClick={handleRejectAll}>전체 거부</button>
            </div>
          </div>
        )}
        <div className="listwrap">
          {floor.requestList.map((userId) => {
            const participant = participants.find((p) => p.id === userId);
            const name = participant?.displayName ?? userId;
            return (
              <div key={userId} className="noti_list hand">
                <div className="ico_noti">
                  <span className="sp_rtc" />
                </div>
                <div className="text_part">
                  <strong>발언권 요청</strong>
                  <p>{name}</p>
                </div>
                <div className="btn_part">
                  <button
                    type="button"
                    onClick={() => onGrantFloorTarget(userId, 'accept')}
                  >
                    수락
                  </button>
                  <button
                    type="button"
                    className="reject"
                    onClick={() => onGrantFloorTarget(userId, 'reject')}
                  >
                    거부
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 모더레이터인데 요청 없으면 표시 안 함
  if (isModerator) return null;

  // ─── 일반 참여자 뷰: 발언권 요청/상태 팝업 ──────────────────────────────────

  // 발언권 모드가 켜져 있고, 요청/승인 상태가 아닐 때 → 요청 버튼
  if (floor.isEnabled && !floor.isRequesting && !floor.isGranted) {
    return (
      <div className="notiwrap rtcCustoms_floorWrap">
        <div className="noti_list">
          <div className="ico_noti">
            <span className="sp_rtc" />
          </div>
          <div className="text_part">
            <p>발언권 모드가 활성화되었습니다.</p>
          </div>
          <div className="btn_part">
            <button type="button" onClick={onRequestFloor}>
              발언 요청
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 요청/승인 상태일 때
  if (!floor.isRequesting && !floor.isGranted) return null;

  let popupMessage = '';
  let popupType = '';
  let buttonLabel = '';
  let buttonAction: (() => void) | null = null;

  if (floor.isGranted) {
    popupMessage = '발언권이 승인되었습니다.';
    popupType = '';
    buttonLabel = '발언 종료';
    buttonAction = () => {
      onStopFloor();
      dispatch(floorActions.setGranted(false));
    };
  } else if (floor.isRequesting) {
    popupMessage = '발언권 요청 중입니다...';
    popupType = 'wait';
    buttonLabel = '요청 취소';
    buttonAction = () => {
      onStopFloor();
      dispatch(floorActions.setRequesting(false));
    };
  }

  return (
    <div className="notiwrap rtcCustoms_floorWrap">
      <div className={`noti_list ${popupType}`}>
        <div className="ico_noti">
          <span className="sp_rtc" />
        </div>
        <div className="text_part">
          <p>{popupMessage}</p>
        </div>
        {buttonAction && (
          <div className="btn_part">
            <button type="button" onClick={buttonAction}>
              {buttonLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloorControl;
